/**
 * SEO Utilities
 *
 * Pure utility functions for SEO-related operations like canonical URLs and hreflang validation.
 */

/**
 * Generates a canonical URL for SEO purposes
 * @param host - The host domain
 * @param pageLocale - The page locale (e.g., 'en-US')
 * @param pagePath - The page path
 * @param pageCanonicalUrl - Optional explicit canonical URL
 * @returns The canonical URL
 */
export const getCanonicalUrl = (
	host: string,
	pageLocale: string,
	pagePath: string,
	pageCanonicalUrl: string
): string => {
	if (pageCanonicalUrl) return pageCanonicalUrl

	if (pagePath === '/') return `https://${host}/${pageLocale}`

	const pageUrlWithParams = `https://${host}/${pageLocale}${pagePath}`
	const pageUrl = pageUrlWithParams.split('?')[0] ?? ''

	if (pageUrlWithParams.includes('page=')) {
		const url = new URL(pageUrlWithParams)
		const searchParams = new URLSearchParams(url.search)
		const pageNumber = searchParams.get('page')

		if (pageNumber === '1') return pageUrl

		if (pageNumber) return `${pageUrl}?page=${pageNumber}`
	}

	return pageUrl
}

/**
 * Validates hreflang tags for XSS prevention and proper format
 * @param pageHrefLangs - The hreflang HTML string
 * @returns true if valid, false otherwise
 */
export const validHrefLangs = (pageHrefLangs: string): boolean => {
	// prevent XSS
	const BAD_WORDS = ['script', 'iframe']

	for (const word of BAD_WORDS) {
		if (pageHrefLangs.includes(word)) return false
	}

	// otherwise proceed
	const hrefLangs = pageHrefLangs.split('/>').slice(0, -1)
	for (const hrefLang of hrefLangs) {
		const hreflangWithoutWhiteSpaces = hrefLang.replace(/\s/g, '')
		// removed white spaces (since white spaces doesn't create an issue), and checking for a particular pattern of attributes that we want inside <link> tag
		if (hreflangWithoutWhiteSpaces) {
			if (
				hreflangWithoutWhiteSpaces.match(/</g)?.length !== 1 ||
				!/<linkrel=(?:"[^"]*"|'[^']*')href=(?:"[^"]*"|'[^']*')hreflang=(?:"[^"]*"|'[^']*')+/.test(
					hreflangWithoutWhiteSpaces
				)
			) {
				return false
			}
		}
	}
	return true
}
